import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, map } from 'rxjs';

import { SignalStore } from '../../shared/services/signal-store';
import { ApiService } from '../../shared/services/api.service';
import { UserService } from '../../shared/services/user.service';
import { SessionService } from '../../shared/services/session.service';
import { Institution, InstitutionService } from './institution.service';
import { Group, GroupInstitution, GroupService, UpdateGroup } from './group.service';
import { CorrelationRate } from './rate.service';

export interface MaintenanceModel {
  maintenanceMode: boolean;
  maintenanceMessage?: string;
}

export interface State {
  institution: Institution;
  peer: Peer;
  dateRange: DateRange;
}

export interface Peer {
  institution?: Institution;
  group?: Group;
}

export enum SubjectType {
  institution = 1,
  peer = 2
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}
export interface PrintState {
  institution: Institution;
  peer?: Peer;
  group?: Group;
  filteredGroup?: GroupInstitution[];
  peerInstitution?: Institution;
  peerInstitutions?: Institution[];
  dateRange?: DateRange;
  date?: Date;
  rate?: CorrelationRate;
}

@Injectable({
  providedIn: 'root'
})
export class StateService extends SignalStore<State> {
  private apiService = inject(ApiService);
  private userService = inject(UserService);
  private institutionService = inject(InstitutionService);
  private groupService = inject(GroupService);
  private sessionService = inject(SessionService);

  private user = this.userService.state;
  private institutionsFetched = this.institutionService.institutionsFetched;

  private _institution: Institution = {} as Institution;
  private _peer?: Peer;
  private _group?: Group;
  private _filteredGroup: GroupInstitution[] = [];
  private _peerInstitution?: Institution;
  private _peerInstitutions: Institution[] = [];
  private _dateRange?: DateRange;
  private _date?: Date;
  private _rate?: CorrelationRate;

  readonly maintenanceModel = signal<MaintenanceModel | undefined>(undefined);
  readonly institution = signal(this._institution);
  readonly peer = signal(this._peer);
  readonly group = signal(this._group);
  readonly filteredGroup = signal(this._filteredGroup);
  readonly peerInstitution = signal(this._peerInstitution);
  readonly peerInstitutions = signal(this._peerInstitutions);
  readonly dateRange = signal(this._dateRange);
  readonly date = signal(this._date);
  readonly rate = signal(this._rate);

  readonly peerName = computed(() => {
    this.filteredGroup();
    return this.peer()?.institution?.name
      ?? (this.peer()?.group?.groupType == 30
        ? this.peer()?.group?.name
        : (this.peer()?.group?.institutions.filter(i => i.isSelected).length == 0
          ? undefined
          : this.peer()?.group?.institutions.filter(i => i.isSelected).length == 1 ? this.peer()?.group?.institutions.find(i => i.isSelected)?.name : this.peer()?.group?.name));
  });

  readonly userInstitutions = signal<Institution[]>([]);
  readonly groups = signal<Group[]>([]);

  readonly isPrinting = signal(false);
  readonly printInfo = signal(false);

  private printSessionCache = new Map<string, object | string | number | boolean>();

  constructor() {
    super();

    this.apiService.get<MaintenanceModel>('api/auth/maintenance').subscribe(maintenanceModel => {
      this.maintenanceModel.set(maintenanceModel);
    });

    effect(() => {
      if (this.user().id && this.institutionsFetched() && !this.isPrinting()) {
        setTimeout(() => {
          const institution = this.institutionService.getInstitution(this.user().institutionIid) ?? {} as Institution;
          this.setInstitution(institution);
        });

        this.institutionService.getUserInstitutions().subscribe(userInstitutions => {
          const institutions: Institution[] = [];
          userInstitutions.forEach(u => {
            const institution = this.institutionService.getInstitution(u.iid);
            if (institution) {
              institution.dateLastViewed = u.dateLastViewed;
              institutions.push(institution);
            }
          });
          institutions.sort((a, b) => a.name?.localeCompare(b.name) || a.city?.localeCompare(b.city) || a.state?.localeCompare(b.state));
          this.userInstitutions.set(institutions);
        });
      }
    });
  }

  public setInstitution(institution: Institution): void {
    this._institution = institution;

    this.groupService.getGroups(this._institution.iid).subscribe(groups => {
      this.institution.set(this._institution);
      this.groups.set(groups);
      let peer = this.sessionService.get(`peer${this._institution?.iid}`) as Peer;
      if (!peer) {
        peer = { institution: undefined, group: groups[0] };
      }
      const group = peer.group ?? groups[0];
      this._group = group;
      this.setGroup(this._group);

      this._filteredGroup = group.institutions;
      this.setFilteredGroup(this._filteredGroup);

      this._peer = peer;
      this.setPeer(peer);
    });

    const peerInstitutions = this.sessionService.get(`peerInstitutions-${this._institution.iid}`) as Institution[] ?? [];
    this.setPeerInstitutions(peerInstitutions);

    this.sessionService.set('institution', institution, true);
    this.userService.setInstitution(institution.iid);
  }

  public setPeer(peer: Peer): void {
    if (peer.group) {
      this.setGroup(peer.group);
      this._filteredGroup = peer.group.institutions;
      this.setFilteredGroup(this._filteredGroup);
    }
    if (peer.institution) {
      if (!this._peerInstitutions.find(i => i.iid == peer.institution?.iid)) {
        this._peerInstitutions = [];
        this.setPeerInstitutions([peer.institution]);
      }
      this.peerInstitution.set(peer.institution);
    }

    this.sessionService.set(`peer${this._institution?.iid}`, peer, true);
    this._peer = peer;
    this.peer.set(this._peer);

    if (this._institution && this._peer && this._dateRange) {
      this.setState({ institution: this._institution, peer: this._peer, dateRange: this._dateRange });
    }
  }

  public setGroup(group: Group): void {
    this._group = group;
    this.group.set(this._group);
  }

  public setFilteredGroup(filteredGroup: GroupInstitution[]): void {
    this._filteredGroup = filteredGroup.slice();
    this.filteredGroup.set(this._filteredGroup);
  }

  public setPeerInstitution(peerInstitution: Institution): void {
    this._peerInstitution = peerInstitution;
    this.peerInstitution.set(this._peerInstitution);
  }

  public setPeerInstitutions(peerInstitution: Institution[]): void {
    this._peerInstitutions = peerInstitution.slice();
    this.sessionService.set(`peerInstitutions-${this._institution.iid}`, this._peerInstitutions, true);
    this.peerInstitutions.set(this._peerInstitutions);
  }

  public setDateRange(dateRange: DateRange): void {
    if (dateRange.startDate.getTime() != this._dateRange?.startDate.getTime() || dateRange.endDate.getTime() != this._dateRange?.endDate.getTime()) {
      this._dateRange = dateRange;
      this.dateRange.set(this._dateRange);
      this.sessionService.set(`dateRange-${this.institution().institutionType}`, this._dateRange, false);

      if (this._institution && this._peer && this._dateRange) {
        this.setState({ institution: this._institution, peer: this._peer, dateRange: this._dateRange });
      }
    }
  }

  public setDate(date: Date): void {
    this._date = date;
    this.date.set(this._date);
  }

  public setRate(rate: CorrelationRate): void {
    this._rate = rate;
    this.rate.set(this._rate);
  }

  public addUserInstitution(institution: Institution) {
    const institutions = this.userInstitutions();
    institutions.push(institution);
    institutions.sort((a, b) => a.name?.localeCompare(b.name) || a.city?.localeCompare(b.city) || a.state?.localeCompare(b.state));
    this.userInstitutions.set(institutions);
    this.apiService.post<void>('api/user/institution', institution).subscribe();
  }

  public removeUserInstitution(institution: Institution) {
    const institutions = this.userInstitutions().filter(i => i.iid !== institution.iid);
    this.userInstitutions.set(institutions);
    this.apiService.delete<void>(`api/user/institution/${institution.iid}`).subscribe();
  }

  updateUserInstitutionLastViewed(institution: Institution) {
    this.apiService.put<void>(`api/user/institution/${institution.iid}`).subscribe();
  }

  createGroup(iid: number, group: Group): Observable<void> {
    return this.groupService.createGroup(iid, group)
      .pipe(map(groups => this.groups.set(groups)));
  }

  updateGroups(iid: number, updateGroups: UpdateGroup[]): Observable<string[]> {
    return this.groupService.updateGroup(iid, updateGroups)
      .pipe(map(groups => {
        this.groups.set(groups);
        return updateGroups.map(g => g.groupId);
      }));
  }

  deleteGroup(iid: number, groupId: string) {
    this.groupService.deleteGroup(iid, groupId).subscribe(() => {
      const groups = this.groups().filter(g => g.groupId !== groupId);
      this.groups.set(groups);
    });
  }

  public getPrintSessionData(key: string): object | string | number | boolean | undefined {
    return this.printSessionCache.get(key);
  }

  public setPrintSessionData(key: string, value: object | string | number | boolean): void {
    this.printSessionCache.set(key, value);
  }

  public storePrintState(showInstitutionsListChkBox: boolean = true) {
    const printState = {
      institution: this.institution(),
      peer: showInstitutionsListChkBox?  this.peer() : null,
      group: this.group(),
      filteredGroup: this.filteredGroup(),
      peerInstitution: this.peerInstitution(),
      peerInstitutions: this.peerInstitutions(),
      dateRange: this.dateRange(),
      date: this.date(),
      rate: this.rate()
    }

    sessionStorage.setItem('printState', JSON.stringify(printState));
    sessionStorage.setItem('printSession', JSON.stringify(Array.from(this.printSessionCache.entries())));
  }

  public loadPrintState(): void {
    const json = sessionStorage.getItem('printState');
    if (json) {
      const printState = JSON.parse(json) as PrintState

      if (printState.institution) {
        this._institution = printState.institution;
        this.institution.set(this._institution);
        this.userService.setInstitution(this._institution.iid);
      }

      if (printState.peer) {
        this._peer = printState.peer;
        this.peer.set(this._peer);
      }

      if (printState.group) this.setGroup(printState.group);
      if (printState.filteredGroup) this.setFilteredGroup(printState.filteredGroup);
      if (printState.peerInstitution) this.setPeerInstitution(printState.peerInstitution);
      if (printState.peerInstitutions) this.setPeerInstitutions(printState.peerInstitutions);
      if (printState.dateRange) {
        const dateRange: DateRange = {
          startDate: new Date(printState.dateRange.startDate),
          endDate: new Date(printState.dateRange.endDate)
        }
        this.setDateRange(dateRange);
      }
      if (printState.date) {
        const date: Date = new Date(printState.date);
        this.setDate(date);
      }
    }

    const printSession = sessionStorage.getItem('printSession');
    if (printSession) {
      this.printSessionCache = new Map(JSON.parse(printSession));
    }
  }
}

